import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { duplicateProjectActivityGroup } from "../../../api/project_activity";

/**
 * Dialog before duplicating a group: suggests a new name (e.g. «Navn (kopi)»).
 */
export function ProjectActivityGroupDuplicateDialog({
  open,
  onClose,
  projectId,
  group,
  onDuplicated,
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = (group?.title || "").trim();
    setTitle(
      base
        ? t("project.activities.group_duplicate_name_suggestion", { name: base })
        : ""
    );
  }, [open, group, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error(t("project.activities.group_title_required"));
      return;
    }
    if (!projectId || !group?.id) return;

    setSaving(true);
    try {
      const result = await duplicateProjectActivityGroup(projectId, group.id, {
        title: trimmed,
      });
      toast.success(t("project.activities.group_duplicated"));
      onDuplicated?.(result);
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error(t("project.activities.group_duplicate_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={saving ? undefined : onClose}
      title={t("project.activities.group_duplicate")}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-slate-600">
          {t("project.activities.group_duplicate_hint")}
        </p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {t("project.activities.group_title")}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("project.activities.group_title_placeholder")}
            autoFocus
            disabled={saving}
            className="block w-full rounded-md border-slate-300 shadow-sm focus:border-[#1463C7] focus:ring-[#1463C7] sm:text-sm disabled:opacity-60"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm disabled:opacity-60"
            style={{ background: "#1463C7" }}
          >
            {saving
              ? t("project.activities.duplicating")
              : t("project.activities.group_duplicate_confirm")}
          </button>
        </div>
      </form>
    </PMDialog>
  );
}
